import type {
  AttendeeContinuingEducationUpdateRequest,
  AttendeeContinuingEducationPolicyView,
  AttendeePresenceState,
  ContinuingEducationRecord,
  PublicConventionGuide,
  PublicScheduleItem,
} from "@verity-connect/contracts";
import {
  MAX_CONTACT_HOURS_PER_CONVENTION,
  WORKSHOP_CATEGORIES,
  formatConventionDates,
  voucherFileName,
  mayBeListedForCredit,
  proposedCategory,
  voucherTotal,
  type CreditableKind,
  type WorkshopCategoryNumber,
} from "@verity-connect/domain";
import { useEffect, useState } from "react";
import { Link } from "./router";
import { twelveHour } from "./clock";
import { useVoucherSelection } from "./continuing-education-record";
import { personalWorkspaceName } from "./association-presentation";

/**
 * The AACS Continuing Education Attendance Voucher, filled in.
 *
 * The form is the teacher's own document: she lists what she attended, signs it,
 * has an administrator sign to verify she was there, keeps it, and mails it with
 * her certification renewal whenever that falls due. A signed-in teacher may
 * send the session list to her school for review; private learning notes and
 * survey answers are not part of that record.
 *
 * The app fills in everything it can honestly know — her name, her school, the
 * convention dates, each session title and a proposed category — and asks her
 * for the one thing it cannot know: which sessions she actually sat in. Saving a
 * session to an agenda in August is a plan, not attendance, so every row starts
 * unticked.
 */
/** The shell's document bridge, when this is a shell that has one. */
export interface ShellDocuments { share(path: string, fileName: string): void }

function shellDocuments(): ShellDocuments | null {
  if (typeof window === "undefined") return null;
  const bridge = (window as unknown as { VerityConnectDocument?: ShellDocuments }).VerityConnectDocument;
  return typeof bridge?.share === "function" ? bridge : null;
}

/**
 * How this device can put the certificate into a share sheet, if it can at all.
 *
 * Measured in both shells on 13 September rather than assumed. An iPhone does it from
 * the page: `navigator.share` accepts a file inside a `WKWebView`, and the sheet carries
 * Print and Save to Files. Android cannot — `navigator.share` is undefined there — so the
 * shell does it instead, through the bridge that reached Play as version code 8.
 *
 * A desktop browser gets nothing, and should: the PDF viewer it opens the certificate in
 * already has printing and saving of its own, and a second set of buttons beside them is
 * clutter that has to be explained.
 */
export function certificateShareRoute(): "shell" | "web" | "none" {
  if (shellDocuments()) return "shell";
  if (typeof navigator !== "undefined" && typeof navigator.share === "function") return "web";
  return "none";
}

export function ContinuingEducation({ guide, savedItemIds, participant, search = "" }: {
  guide: PublicConventionGuide;
  savedItemIds: readonly string[];
  /** The address she arrived by. `?attention=today` puts today's unchecked saves first. */
  search?: string;
  participant: { displayName: string; email: string; personId?: string; tenantId?: string } | null;
}) {
  const { selection, toggleAttended, setCategory, save: saveOnDevice } = useVoucherSelection({
    // Event ids are globally unique and therefore keep this fallback isolated
    // until the attendee session exposes its association id to this screen.
    tenantId: participant?.tenantId ?? "event-owner",
    eventId: guide.event.id,
    personId: participant?.personId ?? participant?.email ?? "signed-out",
  });
  const attended = new Set(selection.attendedItemIds);
  const saved = new Set(savedItemIds);

  /*
   * The seminars she asked for after missing them.
   *
   * Held out of this form entirely rather than shown unticked. The voucher is an attendance
   * document — she signs it and an administrator signs to verify she was there — and a
   * session she studied is one nobody can verify she attended, because she did not. Leaving
   * it on the page with an empty box would be inviting a mistake that two people sign.
   *
   * Read rather than assumed: a teacher can both attend a seminar and later ask for its
   * material, and attending outranks asking.
   */
  const [studied, setStudied] = useState<ReadonlySet<string>>(new Set());
  useEffect(() => {
    if (!participant) return;
    const controller = new AbortController();
    void fetch(`/api/attendee/events/${encodeURIComponent(guide.event.slug)}/studied-sessions`, {
      credentials: "same-origin",
      signal: controller.signal,
    }).then(async (response) => {
      if (!response.ok) return;
      const body = await response.json() as { itemIds?: readonly string[] };
      setStudied(new Set(body.itemIds ?? []));
    }).catch(() => {
      // A form she can still fill in by hand is better than one that refuses to draw.
    });
    return () => controller.abort();
  }, [guide.event.slug, participant]);

  const blockOrder = new Map(guide.blocks.map((block, index) => [block.id, index]));
  const audienceLabel = (item: PublicScheduleItem) =>
    item.audienceIds
      .map((id) => guide.audiences.find((candidate) => candidate.id === id)?.label)
      .filter((label): label is string => Boolean(label));

  // Only seminars can be listed, and only ones she chose. A teacher who saved
  // nothing sees the whole seminar list instead, because the form is still hers
  // to fill in and an empty page would be a dead end.
  const creditable = guide.items.filter((item) => mayBeListedForCredit(
    item.kind as CreditableKind,
    studied.has(item.id) && !saved.has(item.id) ? "studied" : "attended",
  ));
  const offered = creditable.filter((item) => saved.has(item.id));
  /*
   * Arriving from the evening card, the seminars she saved today and has not checked in to
   * come first, so a two-tap fix is a two-tap fix rather than a hunt through fifty-six rows.
   * Everything else keeps its timetable order underneath.
   */
  const attentionToday = new URLSearchParams(search).get("attention") === "today";
  const todayDate = new Intl.DateTimeFormat("en-CA", { timeZone: guide.event.timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
  // Worked out only on the way in from the evening card; on every other visit nothing here runs.
  const todayBlockIds = attentionToday
    ? new Set(guide.blocks.filter((block) => guide.days.find((day) => day.id === block.dayId)?.date === todayDate).map((block) => block.id))
    : new Set<string>();
  const needsAttention = (item: PublicScheduleItem) =>
    attentionToday && todayBlockIds.has(item.blockId) && saved.has(item.id) && !markedInTheRoom.has(item.id) && !attended.has(item.id);
  const rows = (offered.length > 0 ? offered : creditable)
    .slice()
    .sort((left, right) => (Number(needsAttention(right)) - Number(needsAttention(left)))
      || (blockOrder.get(left.blockId) ?? 0) - (blockOrder.get(right.blockId) ?? 0));
  const attentionCount = rows.filter(needsAttention).length;

  /*
   * Grouped by the block it was in, in the order the convention ran.
   *
   * This was one flat list of every seminar at the convention, with the ones she had saved
   * and not ticked yanked to the top and out of order. Sixty-six rows, no headings, and no
   * way to tell Seminar 1 from Seminar 4 — a teacher filling in a form she signs could not
   * see whether she had answered for a block at all.
   *
   * The day is carried on the heading only when the convention runs more than one, because
   * "Thursday · Seminar 1" on every heading of a one-day event is noise.
   */
  const days = guide.days ?? [];
  const dayOf = new Map(days.map((day) => [day.id, day]));
  const manyDays = days.length > 1;
  const sections = guide.blocks
    .map((block) => ({
      block,
      day: dayOf.get(block.dayId) ?? null,
      items: rows.filter((item) => item.blockId === block.id),
    }))
    .filter((section) => section.items.length > 0);

  const categoryOf = (item: PublicScheduleItem): WorkshopCategoryNumber =>
    selection.categoryByItemId[item.id] ?? proposedCategory(audienceLabel(item));

  const lines = rows
    .filter((item) => attended.has(item.id))
    .map((item) => ({ itemId: item.id, title: item.title, category: categoryOf(item) }));
  const total = voucherTotal(lines);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [record, setRecord] = useState<ContinuingEducationRecord | null>(null);
  const [policyAvailable, setPolicyAvailable] = useState<boolean | null>(null);
  const [markedInTheRoom, setMarkedInTheRoom] = useState<ReadonlySet<string>>(new Set());
  const savedSelectionMatches = Boolean(record)
    && record?.lines.length === lines.length
    && record.lines.every((line, index) => line.itemId === lines[index]?.itemId && line.category === lines[index]?.category);
  const [loaded, setLoaded] = useState(participant === null);
  const recordPath = `/api/attendee/events/${encodeURIComponent(guide.event.slug)}/continuing-education`;
  const presencePath = `/api/attendee/events/${encodeURIComponent(guide.event.slug)}/presence`;

  useEffect(() => {
    if (!participant) { setLoaded(true); setRecord(null); setPolicyAvailable(null); return; }
    const controller = new AbortController();
    setLoaded(false);
    setPolicyAvailable(null);
    void Promise.all([
      fetch(recordPath, { credentials: "same-origin", signal: controller.signal }),
      fetch(`${recordPath}/policy`, { credentials: "same-origin", signal: controller.signal }),
    ]).then(async ([recordResponse, policyResponse]) => {
        if (!recordResponse.ok) throw new Error(await responseMessage(recordResponse));
        if (!policyResponse.ok) throw new Error(await responseMessage(policyResponse));
        const [next, policyView] = await Promise.all([
          recordResponse.json() as Promise<ContinuingEducationRecord | null>,
          policyResponse.json() as Promise<AttendeeContinuingEducationPolicyView>,
        ]);
        if (!controller.signal.aborted) {
          setPolicyAvailable(policyView.available);
          setRecord(next);
          if (next) saveOnDevice({
            attendedItemIds: next.lines.map((line) => line.itemId),
            categoryByItemId: Object.fromEntries(next.lines.map((line) => [line.itemId, line.category])),
          });
          setLoaded(true);
        }
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted) { setError(message(reason)); setLoaded(true); }
      });
    return () => controller.abort();
  }, [participant?.email, recordPath, saveOnDevice]);

  /*
   * What she scanned in the rooms.
   *
   * The scan is the record. A seminar she marked from the doorway arrives here
   * already ticked, so this screen is a review of what she did rather than a
   * memory test three weeks later. She can still untick one — the voucher carries
   * her signature, so the last word stays hers — but nothing starts blank that
   * she already answered in the building.
   */
  useEffect(() => {
    if (!participant) { setMarkedInTheRoom(new Set()); return; }
    const controller = new AbortController();
    void fetch(presencePath, { credentials: "same-origin", signal: controller.signal })
      .then(async (response) => response.ok ? await response.json() as AttendeePresenceState : null)
      .then((state) => {
        if (!state || controller.signal.aborted) return;
        const marked = new Set(state.marks.map((mark) => mark.itemId));
        setMarkedInTheRoom(marked);
        const creditable = new Set(
          // "attended", always: she was in the room with the paper in front of her,
          // which is the one standing a scan can mean.
          guide.items
            .filter((item) => mayBeListedForCredit(item.kind as CreditableKind, "attended"))
            .map((item) => item.id),
        );
        /*
         * Every way of having been somewhere, not only the square on the door.
         *
         * This proposed from the scanned squares alone, so a teacher who saved six seminars
         * and sat through all six was handed an empty form and told to fill it in herself.
         * On 17 September 233 people had saved a session and 164 had scanned anything.
         *
         * Every one of these is a proposal on a form she reads, corrects and signs, and any
         * of them can be taken off again.
         */
        const leftATrace = new Set([...marked, ...(state.evidence ?? []).map((one) => one.itemId)]);
        const missing = [...leftATrace].filter((itemId) => creditable.has(itemId) && !attended.has(itemId));
        if (missing.length > 0) {
          for (const itemId of missing) toggleAttended(itemId);
        }
      })
      .catch(() => { /* The voucher still works from her own ticks. */ });
    return () => controller.abort();
    // Deliberately keyed on who is signed in and nothing else: re-running this on
    // every tick would put back a seminar she had just taken off.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participant?.email, presencePath]);

  /*
   * Saving a correction, and nothing more. Whether her school sees this record is
   * not a question this screen can ask: she works for the school, the voucher is
   * the record of that work, and the server reads the answer off her registration
   * whatever is sent here. The flag stays on the wire because the contract is
   * shared with the organizer correction path, and it is sent true so that no
   * client can ever be the thing that withdraws a record from a school's queue —
   * which is what every caller here used to do by passing false.
   */
  const saveRecord = async () => {
    if (!participant) return;
    setSaving(true); setError(null);
    try {
      const body: AttendeeContinuingEducationUpdateRequest = {
        revision: record?.revision ?? 0,
        lines: lines.map((line) => ({ itemId: line.itemId, category: line.category })),
        readyForSchool: true,
      };
      const response = await fetch(recordPath, {
        method: "PUT", credentials: "same-origin", headers: { "content-type": "application/json" }, body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      setRecord(await response.json() as ContinuingEducationRecord);
    } catch (reason) { setError(message(reason)); } finally { setSaving(false); }
  };

  /**
   * Opening the filled form.
   *
   * The page used to build the PDF here and hand it to a hidden download link. That
   * works in a browser and does nothing at all inside the iPhone and Android
   * applications: a file reaches an application through WKDownloadDelegate on iOS and
   * setDownloadListener on Android, and neither shell implements one, so the button was
   * dead for anybody using the app rather than the browser — silently, with no error.
   *
   * The Worker fills the form now and serves it from an ordinary address on our own
   * host, so this is a page the web view opens like any other. Whatever the platform
   * offers for saving and printing a PDF is then offered on top of it.
   *
   * Her ticks are saved first, because the Worker builds the form from the saved record
   * — the same one her school reviews — rather than from whatever is on this screen.
   */
  const [emailed, setEmailed] = useState<string | null>(null);

  /**
   * Whether this phone can put the certificate into a share sheet.
   *
   * Two different answers, measured in the shells on 13 September rather than assumed.
   * An iPhone can do it from the page: `navigator.share` accepts a file inside a
   * `WKWebView` and the sheet it opens carries Print and Save to Files. Android cannot —
   * `navigator.share` is undefined there — so the shell does it, through a bridge that
   * reached Play as version code 8.
   *
   * Neither exists in a desktop browser, and nothing is offered there, because the PDF
   * viewer a browser opens already has saving and printing in it.
   */
  const route = certificateShareRoute();
  const nativeShare = route === "shell" ? shellDocuments() : null;
  const canShare = route !== "none";

  const shareCertificate = async () => {
    if (!participant) return;
    setBusy(true);
    setError(null);
    setEmailed(null);
    try {
      if (!savedSelectionMatches) await saveRecord();
      const path = `${recordPath}/voucher.pdf`;
      const fileName = voucherFileName(
        record?.snapshot.participantName ?? participant.displayName,
        formatConventionDates(guide.event.startDate, guide.event.endDate),
      );
      if (nativeShare) {
        // The shell fetches it with the web view's own cookies and opens Android's sheet.
        nativeShare.share(path, fileName);
        return;
      }
      const response = await fetch(path, { credentials: "same-origin" });
      if (!response.ok) throw new Error(await responseMessage(response));
      const file = new File([await response.blob()], fileName, { type: "application/pdf" });
      await navigator.share({ files: [file], title: "AACS continuing education voucher" });
    } catch (reason) {
      // Dismissing the sheet rejects with AbortError. She chose that; it is not a fault.
      if (reason instanceof DOMException && reason.name === "AbortError") return;
      setError(message(reason));
    } finally {
      setBusy(false);
    }
  };


  /**
   * Sending it to herself, which is what an Android teacher has instead of a share sheet.
   *
   * Measured in the Android shell on 13 September: navigator.share is undefined,
   * window.print() prints nothing, a download saves nothing, and the page cannot escape
   * to a browser. The shell that can share a file is in review. Her own mail application
   * can already print, save and forward, and the form has to reach an administrator for a
   * signature anyway.
   *
   * Offered to everybody, not only Android. Somebody on an iPhone may want it in her
   * inbox too, and a button that appears on one phone and not another is a support
   * question nobody needs during a convention.
   */
  const emailCertificate = async () => {
    if (!participant) return;
    setBusy(true);
    setError(null);
    setEmailed(null);
    try {
      if (!savedSelectionMatches) await saveRecord();
      const response = await fetch(`${recordPath}/voucher/email`, { method: "POST", credentials: "same-origin" });
      if (!response.ok) throw new Error(await responseMessage(response));
      const body = await response.json() as { sentTo?: string };
      setEmailed(body.sentTo ?? null);
    } catch (reason) {
      setError(message(reason));
    } finally {
      setBusy(false);
    }
  };

  const openCertificate = async () => {
    if (!participant) return;
    setBusy(true);
    setError(null);
    try {
      if (!savedSelectionMatches) await saveRecord();
      window.location.assign(`${recordPath}/voucher.pdf`);
    } catch (reason) {
      setError(message(reason));
      setBusy(false);
    }
  };

  // This page holds a named professional-development record. Nathan's later
  // August 19 decision requires an account before the page draws; saving the
  // work to that account remains optional until the teacher sends it to school.
  if (participant === null) {
    return (
      <div className="page credit-page">
        <Link className="back-link subpage-back" href="/my-wvcea">← {personalWorkspaceName()}</Link>
        <section className="personal-empty">
          <p>Continuing education</p>
          <h1 data-page-heading tabIndex={-1}>Sign in to download your voucher</h1>
          <p>It carries your name, your school and the seminars you sat in, so it has to come from your account.</p>
          <Link className="primary-action" href="/account">Sign in</Link>
        </section>
      </div>
    );
  }

  if (!loaded) {
    return <div className="page credit-page"><Link className="back-link subpage-back" href="/my-wvcea">← {personalWorkspaceName()}</Link><div className="personal-loading" aria-busy="true">Loading continuing education…</div></div>;
  }

  if (policyAvailable !== true) {
    return <div className="page credit-page"><Link className="back-link subpage-back" href="/my-wvcea">← {personalWorkspaceName()}</Link><section className="personal-empty">
      <p>Continuing education</p>
      <h1 data-page-heading tabIndex={-1}>Professional records are not set up for this event</h1>
      <p>The association must choose the approved policy and form before you can prepare or send this record.</p>
    </section></div>;
  }

  return (
    <div className="page credit-page">
      <Link className="back-link subpage-back" href="/my-wvcea">← {personalWorkspaceName()}</Link>
      <section className="page-intro">
        <h1 data-page-heading tabIndex={-1}>Continuing education</h1>
        <p>
          AACS grants one contact hour for each seminar you attend. In each seminar, scan the QR
          code to check in. Every seminar you check in to is already on this form and already with
          your school. Check the category against your own
          endorsement — that is the one thing the app cannot know — and open your voucher whenever
          you need it.
        </p>
      </section>

      <section className="credit-tally" aria-live="polite">
        <div><span>Seminars ticked</span><strong>{total.listed}</strong></div>
        <div><span>Contact hours</span><strong>{total.contactHours}</strong></div>
        {total.cappedAtMaximum && (
          <p className="credit-capped">
            AACS allows at most {MAX_CONTACT_HOURS_PER_CONVENTION} contact hours for one convention.
            You have listed more than that.
          </p>
        )}
      </section>

      {participant && loaded && record && (
        <section className={`credit-review-state ${record.status}`} aria-live="polite">
          <span aria-hidden="true">{record.status === "reviewed" ? "✓" : record.status === "needs_changes" ? "!" : "•"}</span>
          <div><strong>{creditStatus(record.status)}</strong><p>{creditStatusDetail(record)}</p></div>
        </section>
      )}

      {/*
        * No record history here.
        *
        * "Version 3: progress saved · 1 hour · 5:41 PM" is the platform talking about
        * itself. A teacher filling in a form for her school does not need a revision log of
        * her own ticking, and a row of internal version numbers on a page she signs makes
        * the whole thing look like something it is not. Staff still see the full history on
        * her record in the organizer console, which is where somebody answering a question
        * about it actually looks.
        */}

      {/*
        * Two different emptinesses, and an earlier draft of this line confused
        * them: `offered` is empty when she saved no agenda, which says nothing
        * about whether she scanned. A teacher with six scans was being told
        * "nothing scanned yet" directly under her six filled rows.
        */}
      {offered.length === 0 && (
        <p className="credit-note">
          {markedInTheRoom.size === 0
            ? "Nothing checked in yet. Every seminar is listed below — scan the QR code in the room to check in and it fills itself in, or tick one here if you could not."
            : "Every seminar is listed below, so you can add one you attended without checking in."}
        </p>
      )}

      {attentionCount > 0 && (
        <p className="credit-attention" role="status">
          The {attentionCount === 1 ? "seminar" : `${attentionCount} seminars`} you saved today and did not check in to {attentionCount === 1 ? "is" : "are"} first. If you were there, tick {attentionCount === 1 ? "it" : "them"}.
        </p>
      )}
      {sections.map((section) => {
        const ticked = section.items.filter((item) => attended.has(item.id)).length;
        return (
        <section className="credit-block" key={section.block.id}>
          <h3>
            <span>
              {manyDays && section.day ? `${section.day.label} · ` : ""}{section.block.title}
            </span>
            <small>
              {twelveHour(section.block.startTime)}–{twelveHour(section.block.endTime)}
              {ticked > 0 ? ` · ${ticked} ticked` : ""}
            </small>
          </h3>
      <ol className="credit-list">
        {section.items.map((item) => {
          const isAttended = attended.has(item.id);
          return (
            <li key={item.id} className={`credit-row${isAttended ? " attended" : ""}`}>
              <label>
                <input type="checkbox" checked={isAttended} onChange={() => toggleAttended(item.id)} />
                <span>
                  <strong>{item.title}</strong>
                  <small>
                    {markedInTheRoom.has(item.id)
                      ? "Checked in"
                      : audienceLabel(item).join(" · ") || "General"}
                  </small>
                </span>
              </label>
              <label className="credit-category">
                <span className="visually-hidden">Workshop category for {item.title}</span>
                <select
                  value={categoryOf(item)}
                  disabled={!isAttended}
                  onChange={(event) => setCategory(item.id, Number(event.target.value) as WorkshopCategoryNumber)}
                >
                  {/* The form's examples for two of the categories are printed once
                      below the list instead of inside every dropdown, where they
                      pushed the name off a phone screen. */}
                  {WORKSHOP_CATEGORIES.map((category) => (
                    <option key={category.number} value={category.number}>
                      {category.number} — {category.label}
                    </option>
                  ))}
                </select>
              </label>
            </li>
          );
        })}
      </ol>
        </section>
        );
      })}

      {/*
        * Two of the nine categories carry examples on the AACS form, and they were printed
        * here as bare sentences under the list with nothing to say what they were. On a
        * phone they read as stray text somebody had left behind.
        */}
      <p className="credit-legend">
        <b>What two of the category numbers mean</b>
        {WORKSHOP_CATEGORIES.filter((category) => category.note).map((category) => (
          <span key={category.number}>{category.number} — {category.label}: {category.note}</span>
        ))}
      </p>

      <div className="credit-actions">
        {/*
          * One button, and it is not "send". Scanning a room files the seminar and
          * carries it to the school on its own, so there is nothing here for her to
          * decide and nothing for her to remember. This saves a correction she made
          * to a category, which is the only thing on this screen she owns.
          */}
        {participant ? <div className="credit-save-actions">
          <button type="button" className="secondary-action" onClick={() => void saveRecord()} disabled={saving || !loaded}>
            {saving ? "Saving…" : "Save my corrections"}
          </button>
        </div> : <p className="credit-sign-in-note"><strong>Sign in to keep this with your account.</strong> You can still prepare the voucher on this device.</p>}
        <button type="button" className="primary-action" onClick={() => void openCertificate()} disabled={total.listed === 0 || busy || saving}>
          {busy ? "Working…" : "Open my certificate"}
        </button>
        {canShare && (
          <button type="button" className="secondary-action" onClick={() => void shareCertificate()} disabled={total.listed === 0 || busy || saving}>
            Share, print or save
          </button>
        )}
        <button type="button" className="secondary-action" onClick={() => void emailCertificate()} disabled={total.listed === 0 || busy || saving}>
          Email it to me
        </button>
        {emailed && <p className="credit-note" role="status">Sent to {emailed}. Print it, sign it, and have an administrator sign it.</p>}
        {error && <p className="credit-error" role="alert">{error}</p>}
        <p className="credit-note">
          It opens the AACS form itself with your answers on it. Print it, sign it, and have an
          administrator sign it.
        </p>
      </div>
    </div>
  );
}

function creditStatus(status: ContinuingEducationRecord["status"]): string {
  if (status === "ready_for_school") return "Sent to your school";
  if (status === "reviewed") return "Reviewed by your school";
  if (status === "needs_changes") return "Your school asked for a change";
  return "Progress saved";
}

function creditStatusDetail(record: ContinuingEducationRecord): string {
  if (record.status === "reviewed") return `${record.contactHours} ${record.contactHours === 1 ? "hour" : "hours"} reviewed${record.reviewedBy ? ` by ${record.reviewedBy}` : ""}.`;
  if (record.status === "needs_changes") return record.reviewNote ?? "Review the seminars and categories, then send the record again.";
  if (record.status === "ready_for_school") return "Your school can review the seminars and prepare its administrator signature.";
  return "Your selections are available when you sign in on another device.";
}

async function responseMessage(response: Response): Promise<string> {
  const body = await response.json().catch(() => null) as { message?: unknown } | null;
  return typeof body?.message === "string" ? body.message : "That did not work. Try again.";
}

function message(reason: unknown): string {
  return reason instanceof Error ? reason.message : "Your continuing-education record could not be saved.";
}
